<script setup lang="ts">
import { defineQuery } from "groq"
import { NuggetCard } from "#components"
import { buildPersonSchema } from "~/utils/schema"

const route = useRoute()

const PERSON_QUERY =
  defineQuery(`*[_type == "person" && slug.current == $slug]{..., 
    "nuggets": *[_type=='nugget' && references(^._id)]{
      _id,
      displayedId,
      type,
      text,
      slug,
      author->{name, slug},
      book->{title, slug},
      music->{title, slug},
    },
    "books": *[_type=='book' && author._ref == ^._id]{
      _id,
      title,
      slug,
      author->{name, slug},
    },
    "music": *[_type=='music' && artist._ref == ^._id]{
      _id,
      title,
      slug,
      artist->{name, slug},
    }
  }[0]`)
const { data: person, status } = useSanityQuery(PERSON_QUERY, {
  slug: route.params.slug,
})

const siteConfig = useSiteConfig()

useSeoMeta({
  description: `Explore insights, wisdom, and content from ${person.value?.name || "this person"}. Discover their nuggets of wisdom, books, music, and blog posts.`,
})

useSchemaOrg(() => buildPersonSchema(person.value, siteConfig.url))
</script>

<template>
  <div class="container mx-auto mt-32">
    <div v-if="status === 'pending'">Loading...</div>
    <div v-else-if="status === 'error'">Error</div>
    <div v-else-if="status === 'success' && person">
      <h1 class="text-4xl font-bold">{{ person.name }}</h1>
      <PersonStats :person="person" />

      <!-- Books Section -->
      <div v-if="person.books && person.books.length > 0" class="mt-12">
        <h2 class="text-2xl font-bold mb-6">Books</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <NuxtLink
            v-for="book in person.books"
            :key="book._id"
            :to="`/books/${book.slug?.current}`"
            class="block"
          >
            <UCard>
              <h3 class="text-lg font-semibold">{{ book.title }}</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                by {{ book.author?.name }}
              </p>
            </UCard>
          </NuxtLink>
        </div>
      </div>

      <!-- Music Section -->
      <div v-if="person.music && person.music.length > 0" class="mt-12">
        <h2 class="text-2xl font-bold mb-6">Music</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <NuxtLink
            v-for="song in person.music"
            :key="song._id"
            :to="`/music/${song.slug?.current}`"
            class="block"
          >
            <UCard>
              <h3 class="text-lg font-semibold">{{ song.title }}</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                by {{ song.artist?.name }}
              </p>
            </UCard>
          </NuxtLink>
        </div>
      </div>

      <!-- Nuggets Section -->
      <div v-if="person.nuggets && person.nuggets.length > 0" class="mt-12">
        <h2 class="text-2xl font-bold mb-6">Nuggets</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <NuggetCard
            v-for="nugget in person.nuggets"
            :key="nugget._id"
            :nugget="nugget"
            :status="status"
          />
        </div>
      </div>
    </div>
  </div>
</template>
